import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Menu, X } from 'lucide-react';
import PostList from '../components/PostList';
import Loading from '../components/icon/loading';
import NetworkFeedSidebar from './NetworkFeedSidebar';
import GroupsFeedSidebar from './GroupsFeedSidebar';
import DiscoverFeedSidebar from './DiscoverFeedSidebar';
import LearningProgressFeedSidebar from './LearningProgressFeedSidebar';
import OnlineFeedSidebar from './OnlineFeedSidebar';
import NewPostsBanner from './shared/NewPostsBanner';
import { isFeedNearTop } from '../utils/mergeFeedPosts';
import './userhome.css';

const UserHome = ({
  user,
  posts,
  onRefresh,
  onLoadMore,
  feedHasMore = false,
  feedLoading = false,
  onLike,
  likes,
  userLiked,
  onLogout,
  navigateToProfile,
  feedFilter = 'all',
  onFeedFilterChange,
  pendingNewCount = 0,
  onShowNewPosts,
}) => {
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const loadMoreRef = useRef(null);
  const validPosts = posts || [];

  useEffect(() => {
    const checkScreen = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) setMobileMenuOpen(false);
    };
    checkScreen();
    window.addEventListener('resize', checkScreen);
    return () => window.removeEventListener('resize', checkScreen);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (posts !== undefined) setLoading(false);
  }, [posts]);

  useEffect(() => {
    if (!pendingNewCount || typeof onShowNewPosts !== 'function') return undefined;
    const onScroll = () => {
      if (isFeedNearTop()) onShowNewPosts();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pendingNewCount, onShowNewPosts]);

  const handleObserver = useCallback(
    (entries) => {
      const [entry] = entries;
      if (entry?.isIntersecting && feedHasMore && !feedLoading && typeof onLoadMore === 'function') {
        onLoadMore();
      }
    },
    [feedHasMore, feedLoading, onLoadMore]
  );

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || typeof onLoadMore !== 'function') return undefined;

    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '240px',
      threshold: 0,
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [handleObserver, onLoadMore, validPosts.length]);

  if (!user) {
    return (
      <div className="home-wrapper">
        <p className="login-placeholder">Please log in to view the posts.</p>
      </div>
    );
  }

  return (
    <div className="home-wrapper">
      {loading ? (
        <div className="loading-panel">
          <Loading />
          <p className="loading-text">Pulling in the latest energy...</p>
        </div>
      ) : (
        <div className="home-layout">
          <div className="content-area">
            <div className="panels-and-content">
              <div className="discussion-shell">
                {!isMobile && (
                  <div className="network-rail">
                    <NetworkFeedSidebar
                      user={user}
                      feedFilter={feedFilter}
                      onFeedFilterChange={onFeedFilterChange}
                    />
                    <OnlineFeedSidebar user={user} />
                    <GroupsFeedSidebar user={user} />
                  </div>
                )}

                <div className="main-content discussion-center">
                  <div className="home-main-column">
                    {isMobile && (
                      <div className="mobile-sidebar-bar">
                        <button
                          type="button"
                          className="mobile-menu-trigger"
                          onClick={() => setMobileMenuOpen((open) => !open)}
                          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                          aria-expanded={mobileMenuOpen}
                        >
                          {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
                        </button>
                        <span className="mobile-sidebar-bar-label">Explore</span>
                      </div>
                    )}

                    <div className="home-main-bottom discussion-card">
                      <NewPostsBanner count={pendingNewCount} onClick={onShowNewPosts} />
                      <PostList
                        posts={validPosts}
                        user={user}
                        onReply={() => {}}
                        onRefresh={onRefresh}
                        onLike={onLike}
                        likes={likes}
                        userLiked={userLiked}
                        preserveOrder
                        hidePagination
                      />
                      <div ref={loadMoreRef} className="feed-load-sentinel" aria-hidden="true" />
                      {feedLoading && validPosts.length > 0 && (
                        <div className="feed-loading-more">
                          <Loading />
                          <span>Loading more discussions...</span>
                        </div>
                      )}
                      {!feedHasMore && validPosts.length > 0 && (
                        <p className="feed-end-label">You&apos;re all caught up</p>
                      )}
                    </div>
                  </div>
                </div>

                {isMobile && mobileMenuOpen && (
                  <>
                    <button
                      type="button"
                      className="sidebar-backdrop"
                      aria-label="Close menu"
                      onClick={() => setMobileMenuOpen(false)}
                    />
                    <div className="mobile-sidebar-drawer" role="dialog" aria-modal="true" aria-label="Home side panels">
                      <div className="mobile-sidebar-drawer-header">
                        <h2 className="mobile-sidebar-drawer-title">Explore</h2>
                        <button
                          type="button"
                          className="panel-close"
                          onClick={() => setMobileMenuOpen(false)}
                          aria-label="Close"
                        >
                          <X size={18} />
                        </button>
                      </div>
                      <div className="side-rails-mobile">
                        <NetworkFeedSidebar
                          user={user}
                          feedFilter={feedFilter}
                          onFeedFilterChange={onFeedFilterChange}
                        />
                        <OnlineFeedSidebar user={user} />
                        <GroupsFeedSidebar user={user} />
                        <DiscoverFeedSidebar user={user} />
                        <LearningProgressFeedSidebar user={user} />
                      </div>
                    </div>
                  </>
                )}

                {!isMobile && (
                  <div className="groups-rail">
                    <DiscoverFeedSidebar user={user} />
                    <LearningProgressFeedSidebar user={user} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default UserHome;
